import axios from "axios";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: BACKEND_URL,
    timeout: 30000,
});

/**
 * Synchronous access token cache. Updated by AuthContext via setAuthToken().
 * Used by the request interceptor instead of awaiting supabase.auth.getSession()
 * which can hang and freeze the UI.
 */
let cachedAccessToken = null;
export const setAuthToken = (token) => {
    cachedAccessToken = token || null;
};

// Best-effort init at module load (non-blocking).
supabase.auth.getSession().then(({ data: { session } }) => {
    if (session?.access_token) cachedAccessToken = session.access_token;
}).catch(() => {});

api.interceptors.request.use((config) => {
    if (cachedAccessToken) {
        config.headers.Authorization = `Bearer ${cachedAccessToken}`;
    }
    return config;
});

/**
 * Response interceptor:
 *   1. On 401, attempt ONE refresh of the Supabase session, update the cache,
 *      then replay the original request.
 *   2. If the refresh fails (refresh_token also revoked/expired), force a
 *      hard redirect to /login?session=expired so the user is NEVER stuck on
 *      a page in a phantom-authenticated state.
 *
 * The redirect uses window.location.replace() rather than React Router
 * navigation because the interceptor lives outside the React tree.
 */
let refreshPromise = null;
let isRedirectingToLogin = false;

const refreshSessionOnce = async () => {
    if (!refreshPromise) {
        refreshPromise = (async () => {
            try {
                const { data, error } = await supabase.auth.refreshSession();
                if (error || !data?.session?.access_token) return null;
                cachedAccessToken = data.session.access_token;
                return data.session.access_token;
            } catch (_) {
                return null;
            } finally {
                setTimeout(() => { refreshPromise = null; }, 0);
            }
        })();
    }
    return refreshPromise;
};

const forceLogout = async () => {
    if (isRedirectingToLogin) return;
    isRedirectingToLogin = true;
    cachedAccessToken = null;
    try { await supabase.auth.signOut(); } catch (_) {}
    // Avoid loop if we're already on a public auth page
    const path = window.location.pathname;
    const isOnAuthPage = ["/login", "/register", "/forgot-password"].some((p) => path.startsWith(p));
    if (!isOnAuthPage) {
        window.location.replace("/login?session=expired");
    } else {
        // On auth page already — just clear the flag for future calls
        setTimeout(() => { isRedirectingToLogin = false; }, 1000);
    }
};

api.interceptors.response.use(
    (r) => r,
    async (e) => {
        const original = e.config || {};
        const status = e?.response?.status;
        const url = (original.url || "").toString();

        // Skip auth-recovery for the auth endpoints themselves; they handle their own errors.
        const isAuthEndpoint =
            url.includes("/api/auth/login/") ||
            url.includes("/api/auth/signup/") ||
            url.includes("/api/auth/password-reset/");

        if (status === 401 && !original._retried && !isAuthEndpoint) {
            original._retried = true;
            const newToken = await refreshSessionOnce();
            if (newToken) {
                original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
                return api.request(original);
            }
            // Refresh failed → session is truly dead
            forceLogout();
        }
        return Promise.reject(e);
    },
);
